import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { fetchNotificationPrefs } from 'services/notificationPrefs'
import {
  QUIET_HOURS_STORAGE_KEY,
  SNOOZE_BROADCAST_CHANNEL,
  SNOOZE_STORAGE_KEY,
  activeMutedUntil,
  publishQuietHours,
  publishSnoozeMutedUntil,
  readQuietHoursFromStorage,
  readSnoozeFromStorage,
  type NotificationQuietSyncMessage,
  type QuietHoursSyncPayload,
} from 'utils/notificationSnoozeSync'
import { isLocalTimeInQuietHours } from 'utils/notificationTimezones'

/** Re-check expiry while snoozed — setTimeout alone can be delayed in background tabs. */
const SNOOZE_WATCHDOG_MS = 15_000

const DEFAULT_QUIET: QuietHoursSyncPayload = {
  enabled: false,
  start: null,
  end: null,
  timezone: 'UTC',
}

function normalizeQuietHours(input: {
  enabled?: boolean
  start?: string | null
  end?: string | null
  timezone?: string | null
}): QuietHoursSyncPayload {
  return {
    enabled: Boolean(input.enabled),
    start: input.start ?? null,
    end: input.end ?? null,
    timezone: (input.timezone || 'UTC').trim() || 'UTC',
  }
}

/**
 * Global snooze (`muted_until`) + quiet hours for header bell + Settings.
 * Expiry via setTimeout + watchdog; other tabs via BroadcastChannel + localStorage.
 */
export function useNotificationSnooze(enabled: boolean) {
  // Sync init from localStorage so the first paint already suppresses sandwich/toast
  // (async prefs fetch / useEffect would leave a gap where auto-open still fires).
  const [mutedUntil, setMutedUntilState] = useState<Date | null>(() =>
    enabled ? activeMutedUntil(readSnoozeFromStorage()) : null
  )
  const [quietHours, setQuietHoursState] = useState<QuietHoursSyncPayload>(() =>
    enabled ? readQuietHoursFromStorage() ?? DEFAULT_QUIET : DEFAULT_QUIET
  )
  const [quietTick, setQuietTick] = useState(0)
  const expiryTimerRef = useRef<number | null>(null)

  const clearExpiryTimer = useCallback(() => {
    if (expiryTimerRef.current != null) {
      window.clearTimeout(expiryTimerRef.current)
      expiryTimerRef.current = null
    }
  }, [])

  const clearExpiredSnooze = useCallback(() => {
    setMutedUntilState(null)
    clearExpiryTimer()
    publishSnoozeMutedUntil(null)
  }, [clearExpiryTimer])

  const applyMutedUntil = useCallback(
    (iso: string | null | undefined, options?: { broadcast?: boolean }) => {
      const next = activeMutedUntil(iso ?? null)
      const nextIso = next ? next.toISOString() : null
      setMutedUntilState(prev => {
        const prevIso = prev ? prev.toISOString() : null
        return prevIso === nextIso ? prev : next
      })
      clearExpiryTimer()

      if (next) {
        const delay = Math.max(0, next.getTime() - Date.now())
        expiryTimerRef.current = window.setTimeout(() => {
          expiryTimerRef.current = null
          setMutedUntilState(null)
          publishSnoozeMutedUntil(null)
        }, delay)
      }

      if (options?.broadcast !== false) {
        publishSnoozeMutedUntil(nextIso)
      }
    },
    [clearExpiryTimer]
  )

  const applyQuietHours = useCallback(
    (
      input: {
        enabled?: boolean
        start?: string | null
        end?: string | null
        timezone?: string | null
      },
      options?: { broadcast?: boolean }
    ) => {
      const next = normalizeQuietHours(input)
      setQuietHoursState(prev => {
        if (
          prev.enabled === next.enabled &&
          prev.start === next.start &&
          prev.end === next.end &&
          prev.timezone === next.timezone
        ) {
          return prev
        }
        return next
      })
      publishQuietHours(next, { broadcast: options?.broadcast !== false })
    },
    []
  )

  const refreshSnoozePrefs = useCallback(async () => {
    try {
      const prefs = await fetchNotificationPrefs()
      applyMutedUntil(prefs.muted_until)
      applyQuietHours(
        {
          enabled: Boolean(prefs.quiet_hours_enabled),
          start: prefs.quiet_hours_start,
          end: prefs.quiet_hours_end,
          timezone: prefs.timezone || 'UTC',
        },
        { broadcast: false }
      )
    } catch (err) {
      console.warn('[Notifications] failed to load snooze prefs', err)
    }
  }, [applyMutedUntil, applyQuietHours])

  useEffect(() => {
    if (!enabled) {
      clearExpiryTimer()
      setMutedUntilState(null)
      setQuietHoursState(DEFAULT_QUIET)
      return
    }

    const cachedMute = activeMutedUntil(readSnoozeFromStorage())
    if (cachedMute) {
      // Re-apply so expiry timer is armed (useState already hydrated UI from storage).
      applyMutedUntil(cachedMute.toISOString(), { broadcast: false })
    }
    const cachedQuiet = readQuietHoursFromStorage()
    if (cachedQuiet) {
      applyQuietHours(cachedQuiet, { broadcast: false })
    }
    void refreshSnoozePrefs()
    // Intentionally only when `enabled` flips — apply/refresh are stable enough for cold start.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  useEffect(() => {
    if (!enabled) {
      return
    }

    let channel: BroadcastChannel | null = null
    const onMessage = (event: MessageEvent<NotificationQuietSyncMessage>) => {
      const data = event.data
      if (!data || typeof data !== 'object') {
        return
      }
      if (data.type === 'snooze') {
        applyMutedUntil(data.muted_until, { broadcast: false })
        return
      }
      if (data.type === 'quiet-hours') {
        applyQuietHours(data, { broadcast: false })
      }
    }

    try {
      if (typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel(SNOOZE_BROADCAST_CHANNEL)
        channel.addEventListener('message', onMessage)
      }
    } catch {
      channel = null
    }

    const onStorage = (event: StorageEvent) => {
      if (event.key === SNOOZE_STORAGE_KEY) {
        applyMutedUntil(event.newValue, { broadcast: false })
        return
      }
      if (event.key === QUIET_HOURS_STORAGE_KEY) {
        applyQuietHours(readQuietHoursFromStorage() ?? DEFAULT_QUIET, { broadcast: false })
      }
    }
    window.addEventListener('storage', onStorage)

    const onVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        void refreshSnoozePrefs()
        // Immediate local expiry if deadline already passed (timer may still be pending).
        setMutedUntilState(prev => {
          if (prev && prev.getTime() <= Date.now()) {
            publishSnoozeMutedUntil(null)
            return null
          }
          return prev
        })
      }
    }
    document.addEventListener('visibilitychange', onVisibilityOrFocus)
    window.addEventListener('focus', onVisibilityOrFocus)

    return () => {
      channel?.removeEventListener('message', onMessage)
      channel?.close()
      window.removeEventListener('storage', onStorage)
      document.removeEventListener('visibilitychange', onVisibilityOrFocus)
      window.removeEventListener('focus', onVisibilityOrFocus)
      clearExpiryTimer()
    }
  }, [enabled, applyMutedUntil, applyQuietHours, refreshSnoozePrefs, clearExpiryTimer])

  // Watchdog: clear stale Off icon when setTimeout is delayed (background tab) or missed.
  useEffect(() => {
    if (!enabled || !mutedUntil) {
      return
    }
    const tick = () => {
      if (mutedUntil.getTime() <= Date.now()) {
        clearExpiredSnooze()
      }
    }
    tick()
    const id = window.setInterval(tick, SNOOZE_WATCHDOG_MS)
    return () => window.clearInterval(id)
  }, [enabled, mutedUntil, clearExpiredSnooze])

  useEffect(() => {
    if (!enabled) {
      return
    }
    const id = window.setInterval(() => setQuietTick(n => n + 1), SNOOZE_WATCHDOG_MS)
    return () => window.clearInterval(id)
  }, [enabled])

  // Live check — do not memoize against mutedUntil alone (stale true after deadline until timer fires).
  const isSnoozed = Boolean(mutedUntil && mutedUntil.getTime() > Date.now())
  const isQuietHours = useMemo(
    () =>
      isLocalTimeInQuietHours({
        enabled: quietHours.enabled,
        start: quietHours.start,
        end: quietHours.end,
        timezone: quietHours.timezone,
      }),
    [quietHours, quietTick]
  )

  return {
    mutedUntil: isSnoozed ? mutedUntil : null,
    isSnoozed,
    isQuietHours,
    applyMutedUntil,
    applyQuietHours,
    refreshSnoozePrefs,
  }
}
